import { Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {MatDatepicker, MatDatepickerModule, MatDateRangePicker} from '@angular/material/datepicker';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {provideNativeDateAdapter} from '@angular/material/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';

@Component({
  selector: 'app-datepicker',
  standalone: true,
  providers: [provideNativeDateAdapter()],
  imports: [MatFormFieldModule, MatInputModule, MatDatepickerModule, FormsModule],
  templateUrl: './datepicker.component.html',
  styleUrl: './datepicker.component.scss'
})
export class DatePickerComponent implements OnInit {
  @Input() set initialSelection(value: { from: Date, to: Date }) {
    if (value?.from || value?.to) {
      this.calculateFirstSelectedValue(value);
    } else {
      this.setDatesToDefault();
    }
  };
  selectedOption: string;
  auxiliarText: string;
  today: Date = new Date();

  @Output() selectedDates = new EventEmitter<{ from: Date; to: Date }>();

  @ViewChild('singleDatePicker') private readonly singleDatePicker: MatDatepicker<Date>;
  @ViewChild('dateRangePicker') private readonly dateRangePicker: MatDateRangePicker<Date>;

  private readonly DATE_FORMAT = 'MMM D, YYYY';

  private from: Date;
  private to: Date;
  private previousSelected: string;

  ngOnInit(): void {
    if (!this.selectedOption) {
      this.setDatesToDefault();
    }
  }

  /**
    * Reacts on user chaning selection
  * @param $event selected value
  */
  onChange($event: any): void {
    this.to = undefined;
    switch ($event) {
      case 'dateToNow':
        this.singleDatePicker.open();
        return;
      case 'dateRange':
        this.dateRangePicker.open();
        return;
      case 'allTime':
        this.from = null;
        this.to = null;
        this.confirmDateRangeChanged();
        return;
      default:
        this.setDatesForFixedValue(Number($event));
        this.confirmDateRangeChanged();
    }
  }

  onCalendarClose(): void {
    if (this.to === undefined) {
      this.selectedOption = this.previousSelected;
    }
  }

  onStartDateChange($event: any) {
    this.from = $event.value;
  }

  onEndDateChange($event: any) {
    if($event.value) {
      this.to = $event.value;
      const startFormatted = moment(this.from).format(this.DATE_FORMAT);
      const endFormatted = moment(this.to).format(this.DATE_FORMAT);
      this.auxiliarText = `${startFormatted} – ${endFormatted}`;
      this.selectedOption = 'aux';
      this.confirmDateRangeChanged();
    }
  }

  onSingleDatePickerDateChange($event: any) {
    this.from = $event.value;
    this.to = new Date();
    const startFormatted = moment(this.from).format(this.DATE_FORMAT);
    this.auxiliarText = `${startFormatted} – Now`;
    this.selectedOption = 'aux';
    this.confirmDateRangeChanged();
  }

  /**
   * It changes internal state of from/to dates.
   *
   * @param days How many days from today in the past
   */
  private setDatesForFixedValue(days: number): void {
    this.from = moment().utc().subtract(days, 'days').toDate();
    this.to = new Date();
  }

  private confirmDateRangeChanged(): void {
    this.previousSelected = this.selectedOption;
    this.selectedDates.emit({ from: this.from, to: this.to });
  }

  private calculateFirstSelectedValue({ from = null, to = null }: { from: Date; to: Date }) {
    if (from === null && to === null) {
      this.selectedOption = 'allTime';
      this.previousSelected = 'allTime';
      this.from = null;
      this.to = null;
      this.auxiliarText = '';
      return;
    }

    this.from = this.from || from;
    this.to = this.to || to;

    const today = moment().utc().startOf('day');
    const isToDateToday = moment(to).utc().isSame(today, 'day');
    const fromDateDifference = moment(to).utc().diff(moment(from).utc(), 'day');

    if (isToDateToday && fromDateDifference === 7) {
      this.selectedOption = '7';
      this.auxiliarText = '';
    } else if (isToDateToday && fromDateDifference === 30) {
      this.selectedOption = '30';
      this.auxiliarText = '';
    } else if (isToDateToday && fromDateDifference === 90) {
      this.selectedOption = '90';
      this.auxiliarText = '';
    } else if (isToDateToday && fromDateDifference >= 63999) {
      this.selectedOption = 'allTime';
      this.auxiliarText = '';
    } else if (isToDateToday) {
      this.auxiliarText = `${moment(this.from).utc().format(this.DATE_FORMAT)} – Now`;
      this.selectedOption = 'aux';
    } else {
      this.auxiliarText = `${moment(this.from).utc().format(this.DATE_FORMAT)} – ${moment(this.to).format(this.DATE_FORMAT)}`;
      this.selectedOption = 'aux';
    }
    this.previousSelected = this.selectedOption;
  }

  private setDatesToDefault() {
    this.selectedOption = 'allTime';
    this.previousSelected = 'allTime';
    this.onChange('allTime');
  }
}
